import { mediaUrl } from '../../lib/media-url.js';
import { ShoppingCart, Trash2, ArrowRight, Package } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '../../components/ui/Sheet.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { useCart } from '../../store/cart.jsx';
import { hasUnknownPriceItems } from '../../store/cart-state.js';
import { formatCurrency } from '../../lib/utils.js';
import { useTenant } from '../../context/TenantContext.jsx';
import { isStripeCheckout } from '../../lib/checkout-mode.js';

export function CartDrawer({ open, onOpenChange, onNavigate }) {
  const { items, totalItems, subtotalCents, totalCents, updateQuantity, removeItem } = useCart();
  const { tenant } = useTenant();
  const stripeCheckout = isStripeCheckout(tenant);

  const searchParams = typeof window !== 'undefined' ? new URL(window.location.href).searchParams : new URLSearchParams();
  const isOciMode = searchParams.get('mode') === 'oci';
  const priceUnknown = hasUnknownPriceItems(items);

  const handleCheckout = () => {
    onOpenChange(false);
    if (isOciMode) {
      onNavigate('/cart?mode=oci');
    } else {
      onNavigate('/cart');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col h-full bg-white">
        <SheetHeader className="border-b border-[var(--border)] pb-4">
          <SheetTitle className="flex items-center gap-2 font-display text-lg font-bold text-[var(--foreground)]">
            <ShoppingCart className="h-5 w-5 text-[var(--primary)]" />
            <span>{isOciMode || stripeCheckout ? 'Warenkorb' : 'Anfragekorb'} ({totalItems})</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-6 space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--surface-container-low)] text-[var(--secondary)]">
              <Package className="h-8 w-8" />
            </div>
            <div>
              <p className="font-display text-base font-bold text-[var(--foreground)]">Ihr {isOciMode || stripeCheckout ? 'Warenkorb' : 'Anfragekorb'} ist leer</p>
              <p className="text-xs text-[var(--secondary)] mt-1">Fügen Sie Artikel aus dem Katalog hinzu, um {isOciMode ? 'sie an ES2000 zu übergeben' : 'ein Angebot anzufordern'}.</p>
            </div>
            <SheetClose asChild>
              <Button variant="outline" size="sm" onClick={() => onNavigate(isOciMode ? '/?mode=oci' : '/catalog')}>
                Katalog durchsuchen
              </Button>
            </SheetClose>
          </div>
        ) : (
          <>
            {/* Items List */}
            <div className="flex-1 overflow-y-auto divide-y divide-[var(--border)] py-2">
              {items.map((item) => {
                const sku = item.sku || item.productId || item.id;
                const unitPrice = item.unitPriceCents || 0;
                const lineTotal = unitPrice * item.quantity;

                return (
                  <div key={sku} className="py-4 flex gap-3 items-start">
                    {/* Item Image */}
                    <div className="h-16 w-16 rounded-lg border border-[var(--border)] bg-[var(--surface-container-low)] p-1 flex items-center justify-center shrink-0">
                      {item.primaryImageUrl ? (
                        <img src={mediaUrl(item.primaryImageUrl)} alt="" className="h-full w-full object-contain" />
                      ) : (
                        <Package className="h-6 w-6 text-[var(--secondary)]/50" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 space-y-1">
                      <p className="text-xs font-bold text-[var(--foreground)] truncate">{item.name}</p>
                      <p className="text-xs font-mono text-[var(--secondary)]">Art.-Nr. {item.sku}</p>
                      <p className="text-xs font-semibold text-[var(--foreground)]">
                        {unitPrice > 0 ? formatCurrency(unitPrice) : 'Auf Anfrage'}
                      </p>

                      {/* Quantity buttons */}
                      <div className="flex items-center gap-2 pt-1">
                        <div className="flex items-center rounded border border-[var(--border)] bg-white text-xs">
                          <button
                            onClick={() => updateQuantity(sku, item.quantity - 1)}
                            className="px-2 py-0.5 font-bold hover:bg-[var(--surface-container-low)] cursor-pointer"
                          >
                            -
                          </button>
                          <span className="px-2 font-bold">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(sku, item.quantity + 1)}
                            className="px-2 py-0.5 font-bold hover:bg-[var(--surface-container-low)] cursor-pointer"
                          >
                            +
                          </button>
                        </div>

                        <button
                          onClick={() => removeItem(sku)}
                          className="text-[var(--secondary)] hover:text-red-600 p-1 transition-colors cursor-pointer"
                          title="Entfernen"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Line Total */}
                    <div className="text-right shrink-0 font-bold text-xs">
                      {unitPrice > 0 ? formatCurrency(lineTotal) : 'Auf Anfrage'}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer Summary */}
            <div className="border-t border-[var(--border)] pt-4 space-y-3">
              <div className="flex justify-between text-xs text-[var(--secondary)]">
                <span>Zwischensumme (netto)</span>
                <span className="font-semibold text-[var(--foreground)]">
                  {priceUnknown ? 'Auf Anfrage' : formatCurrency(subtotalCents)}
                </span>
              </div>
              <div className="flex justify-between text-xs text-[var(--secondary)]">
                <span>Geschätzte MwSt. (USt.)</span>
                <span>{!priceUnknown && totalCents > subtotalCents ? formatCurrency(totalCents - subtotalCents) : 'Wird im Angebot ermittelt'}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-[var(--foreground)] border-t border-[var(--border)] pt-2">
                <span>Gesamtbetrag {totalCents > subtotalCents ? '(brutto)' : '(netto)'}</span>
                <span>{priceUnknown ? 'Auf Anfrage' : formatCurrency(totalCents)}</span>
              </div>

              <Button
                className={`w-full gap-2 font-bold shadow-sm cursor-pointer ${isOciMode ? 'bg-sky-600 hover:bg-sky-700 text-white' : ''}`}
                size="lg"
                onClick={handleCheckout}
              >
                {isOciMode ? 'Zur OCI-Übergabe' : stripeCheckout ? 'Zur Kasse' : 'Zur Anfrage / Kasse'}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
